<!-- eslint-disable -->
<template>
  <Button
    v-if="quote?.quote_line_items?.length && !loading"
    :disabled="loading || fetchingMaterials || savingLineItems || !canFetchPricing || quoteIsWonOrRejected"
    class="m-2"
    @click="refreshPricing"
    >Refresh Pricing
  </Button>
</template>

<script setup lang="ts">
/* eslint-disable */
import { toRefs, computed, watch, ref } from "vue";
import type { PropType } from "vue";
import {
  AllQuoteFieldsFragment as QuoteType,
  SkMaterial,
  useSkMaterialsQuery,
  useUpdateManyQuoteLineItemsMutation
} from "gql/api";
import { applyRefreshedLineItemPricing, cleanNull, createQueryString, fixDate } from "@/utils/quoteHelpers";
import { useToast } from "primevue/usetoast";
import { MaterialQueryParams } from "./types";

const toast = useToast();

const props = defineProps({
  quote: {
    type: Object as PropType<QuoteType | null>,
    default: {}
  },
  loading: {
    type: Boolean,
    default: true
  },
  quoteIsWonOrRejected: {
    type: Boolean,
    default: false
  }
});

const savingLineItems = ref(false);
const { quote, loading, quoteIsWonOrRejected } = toRefs(props);

const { executeMutation: updateManyQuoteLineItems } =
  useUpdateManyQuoteLineItemsMutation();

const canFetchPricing = computed(() => {
  return (
    quote.value?.quote_line_items?.length &&
    quote.value?.erp_price_list_number &&
    quote.value?.pricing_date &&
    quote.value?.erp_well_country
  );
});

const {
  data: materialData,
  executeQuery: fetchMaterials,
  fetching: fetchingMaterials,
  error: materialError
} = useSkMaterialsQuery({
  pause: true,
  requestPolicy: "network-only",
  variables: computed(() => {
    const q = quote.value;
    const params: MaterialQueryParams = ({
      country: q?.erp_well_country || undefined,
      deleted: "False",
      exclude_trade_in: "True",
      expand: "pricing",
      ordering: "description,number",
      number__in: cleanNull(
        (q?.quote_line_items || []).map((qli) => qli?.erp_material_number)
      ),
      price_list: q?.erp_price_list_number || undefined,
      pricing_date: fixDate(new Date(q?.pricing_date))
    });

    if (q?.erp_contract_number) {
      params.contract = q?.erp_contract_number;
    }

    return {
      summitQueryString: createQueryString(params)
    };
  })
});

watch(materialError, error => {
  if (error) {
    toast.add({
      severity: "error",
      summary: "Error Fetching Materials",
      detail: error.message,
      life: 7000
    });
  }
});

const refreshPricing = () => {
  // Wrapper for fetchMaterials
  fetchMaterials().then(() => {
    const materials: SkMaterial[] = (materialData?.value?.getSKMaterials || [])
      .filter((material): material is SkMaterial => material !== null);
    const { changedItems, messages } = applyRefreshedLineItemPricing(
      quote?.value?.quote_line_items || [],
      materials,
      { contractActive: !!quote?.value?.erp_contract_number }
    );

    if (changedItems.length === 0) {
      toast.add({
        severity: "info",
        summary: "No Pricing Changes",
        detail: "No pricing changes were found for the quote line items",
        life: 7000
      });
      return;
    }

    toast.add({
      severity: "info",
      summary: "Quote Line Items With New Pricing Updated, Please Save Changes.",
      detail: messages.join("\n"),
      life: 10000
    });
  })
}

</script>

<style scoped lang="scss"></style>
